import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ABOUT } from "@/content";
import { MaskLines, Overline, Reveal } from "@/components/site/Reveal";

const Pillar = ({ label, text, testId, delay }) => (
  <Reveal delay={delay} className="group border-t border-rc-line pt-8" data-testid={testId}>
    <div className="flex items-baseline justify-between">
      <Overline>{label}</Overline>
      <span className="h-2 w-2 rounded-full bg-rc-copper transition-transform duration-500 group-hover:scale-[2.2]" />
    </div>
    <p className="mt-6 font-display text-2xl leading-snug text-rc-ink sm:text-3xl">{text}</p>
  </Reveal>
);

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="about" ref={ref} className="relative mx-auto max-w-[1440px] px-5 py-28 sm:px-8 lg:px-12 lg:py-40" data-testid="about-section">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <Overline>About The Rhett Co</Overline>
          <MaskLines
            lines={["A creative partner", <>for the <em className="italic text-rc-copper">church</em> &amp;</>, "the creative community."]}
            className="mt-8 font-display text-4xl font-medium leading-[1.02] tracking-tight text-rc-ink sm:text-5xl lg:text-6xl"
            testId="about-heading"
          />
          <Reveal delay={0.2} className="mt-10 max-w-2xl space-y-5 text-base leading-relaxed text-rc-body md:text-lg">
            <p>{ABOUT.intro}</p>
            <p>{ABOUT.body}</p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-2">
            <Pillar label="Our Mission" text={ABOUT.mission} testId="about-mission" delay={0.1} />
            <Pillar label="Our Vision" text={ABOUT.vision} testId="about-vision" delay={0.25} />
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5 lg:pt-24">
          <div className="relative overflow-hidden rounded-[28px] border border-rc-line">
            <div className="aspect-[4/5] overflow-hidden">
              <motion.img style={{ y, scale: 1.2 }} src="/images/studio-outboard.webp" alt="Studio outboard gear and mixing console" className="h-full w-full object-cover" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
              <p className="font-display text-2xl italic text-white [text-shadow:0_2px_14px_rgba(0,0,0,0.7)]">Creative excellence.</p>
              <p className="font-mono-rc text-[11px] uppercase tracking-[0.25em] text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.7)]">Studio</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
